<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import Header from './component/header.vue'

const loading  = ref(false)
const cycles   = ref([])
const selCycle = ref('')
const tab      = ref('summary')
const summary  = ref(null)
const dist     = ref([])
const depts    = ref([])
const trends   = ref([])

const loadCycles = async () => {
  const res = await axios.get('/hr/pms/cycles', { params: { per_page: 100 } })
  cycles.value = res.data.data?.data ?? []
}

const loadReport = async () => {
  if (!selCycle.value) return
  try {
    loading.value = true
    if (tab.value === 'summary') {
      const res = await axios.get('/hr/pms/reports/summary', { params: { cycle_id: selCycle.value } })
      summary.value = res.data.data
    } else if (tab.value === 'distribution') {
      const res = await axios.get('/hr/pms/reports/rating-distribution', { params: { cycle_id: selCycle.value } })
      dist.value = res.data.data ?? []
    } else if (tab.value === 'departments') {
      const res = await axios.get('/hr/pms/reports/department-comparison', { params: { cycle_id: selCycle.value } })
      depts.value = res.data.data ?? []
    } else if (tab.value === 'trends') {
      const res = await axios.get('/hr/pms/reports/trends', { params: { cycle_id: selCycle.value } })
      trends.value = res.data.data ?? []
    }
  } catch (e) { console.error(e) } finally { loading.value = false }
}

onMounted(() => { loadCycles() })
watch([selCycle, tab], loadReport)

const pct = (v) => (Number(v ?? 0) * 100).toFixed(1) + '%'
const fmt = (v) => Number(v ?? 0).toFixed(2)
</script>

<template>
  <div>
    <Header />
    <div class="pms-page">
      <div class="pms-toolbar">
        <div class="report-tabs">
          <button v-for="t in ['summary','distribution','departments','trends']" :key="t"
            :class="['r-tab', tab === t ? 'r-tab--active' : '']"
            @click="tab = t">{{ t.charAt(0).toUpperCase() + t.slice(1) }}</button>
        </div>
        <select v-model="selCycle" class="pms-select">
          <option value="">— Select cycle —</option>
          <option v-for="c in cycles" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>
      </div>

      <div v-if="!selCycle" class="empty-state">Select a cycle to view reports.</div>
      <div v-else-if="loading" class="empty-state">Loading…</div>
      <template v-else>

        <!-- Summary -->
        <template v-if="tab === 'summary' && summary">
          <div class="stat-grid">
            <div class="stat-card">
              <div class="stat-label">Total Participants</div>
              <div class="stat-val">{{ summary.total_participants ?? 0 }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Reviews Submitted</div>
              <div class="stat-val">{{ summary.reviews_submitted ?? 0 }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Avg Final Score</div>
              <div class="stat-val">{{ fmt(summary.avg_score) }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Payouts Approved</div>
              <div class="stat-val">{{ summary.payouts_approved ?? 0 }}</div>
            </div>
          </div>
        </template>

        <!-- Rating distribution -->
        <template v-else-if="tab === 'distribution'">
          <div class="table-card">
            <table class="data-table">
              <thead><tr><th>Rating Band</th><th>Count</th><th>%</th></tr></thead>
              <tbody>
                <tr v-if="!dist.length"><td colspan="3" class="tbl-center">No data</td></tr>
                <tr v-for="d in dist" :key="d.band">
                  <td class="bold">{{ d.band }}</td>
                  <td>{{ d.count }}</td>
                  <td>{{ d.percentage ?? pct(d.count / (summary?.total_participants || 1)) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- Department comparison -->
        <template v-else-if="tab === 'departments'">
          <div class="table-card">
            <table class="data-table">
              <thead><tr><th>Department</th><th>Avg Score</th><th>Participants</th><th>Completed</th></tr></thead>
              <tbody>
                <tr v-if="!depts.length"><td colspan="4" class="tbl-center">No data</td></tr>
                <tr v-for="d in depts" :key="d.department">
                  <td class="bold">{{ d.department }}</td>
                  <td>{{ fmt(d.avg_score) }}</td>
                  <td>{{ d.participants }}</td>
                  <td>{{ d.completed }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- Trends -->
        <template v-else-if="tab === 'trends'">
          <div class="table-card">
            <table class="data-table">
              <thead><tr><th>Cycle</th><th>Avg Score</th><th>Participants</th></tr></thead>
              <tbody>
                <tr v-if="!trends.length"><td colspan="3" class="tbl-center">No trend data</td></tr>
                <tr v-for="t in trends" :key="t.cycle_name">
                  <td class="bold">{{ t.cycle_name }}</td>
                  <td>{{ fmt(t.avg_score) }}</td>
                  <td>{{ t.participants }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

      </template>
    </div>
  </div>
</template>

<style scoped>
.pms-page { padding: 0 24px 40px; }
.pms-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
.report-tabs { display: flex; gap: 4px; }
.r-tab { padding: 7px 14px; border: 1px solid #dde3ef; border-radius: 6px; background: #fff; font-size: 13px; cursor: pointer; color: #555; }
.r-tab--active { background: #004AAD; color: #fff; border-color: #004AAD; }
.pms-select { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; }
.empty-state { text-align: center; color: #999; padding: 80px; font-size: 14px; }
.stat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; margin-bottom: 20px; }
.stat-card { background: #fff; border: 1px solid #eaf0fb; border-radius: 10px; padding: 18px 20px; text-align: center; }
.stat-label { font-size: 12px; color: #888; margin-bottom: 8px; }
.stat-val { font-size: 28px; font-weight: 700; color: #1a2a45; }
.table-card { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; }
.tbl-center { text-align: center; color: #999; padding: 40px; }
.bold { font-weight: 600; }
</style>
